import React from 'react';
import { Image, Price, PrimaryLabel } from '@noths/polaris-client-ribbons-design-system';
import {
  getProductImageURL,
  isMiddleMouseClick,
  linkClickOpensInNewTab,
} from '@noths/polaris-client-utils';
import { CurrencyCode, SimilarProduct } from '@noths/polaris-dev-ts-types';

import { RELATED_PREVIOUS_PRICE, RELATED_PRICE } from '.';
import { RelatedProductClickPayload } from '../modules';
import * as styles from '../styles/PartnerStorefront.styles';

export interface PartnerProductCardProps {
  product: SimilarProduct;
  currency: CurrencyCode;
  onRelatedProductClick: (payload: RelatedProductClickPayload) => void;
  lazyImage?: boolean;
}

export const PartnerProductCard = ({
  product,
  currency,
  onRelatedProductClick,
  lazyImage = true,
}: PartnerProductCardProps) => {
  const price = product.prices?.find((x) => x.currency === currency);
  const preSalePrice = product.pre_sale_prices?.find((x) => x.currency === currency);

  const handleLinkClick = (isMousedown: boolean) => (evt: React.MouseEvent<HTMLAnchorElement>) => {
    const opensInNewTab = linkClickOpensInNewTab(evt) || isMiddleMouseClick(evt);

    if (isMousedown && !isMiddleMouseClick(evt)) {
      return;
    }

    if (!opensInNewTab) {
      evt.preventDefault();
    }

    onRelatedProductClick({
      code: product.code,
      href: product.links[0].href || '',
      shouldUseEventCallback: !opensInNewTab,
    });
  };

  return (
    <div key={product.code} css={styles.relatedProductWrapper}>
      <h3 css={styles.productHeading}>
        <a
          css={styles.imageLink}
          href={product.links[0].href || ''}
          onClick={handleLinkClick(false)}
          onMouseDown={handleLinkClick(true)}
        >
          <div css={styles.imageWrapper}>
            <Image
              loading={lazyImage ? 'lazy' : 'eager'}
              css={styles.image}
              src={getProductImageURL(product.images[0].href, 'normal')}
            />
          </div>
          <p css={styles.productTitle}>{product.title}</p>
        </a>
      </h3>
      <PrimaryLabel
        className="similar-product-image__label"
        css={styles.productLabel}
        isNew={product.new}
        onSale={product.on_sale}
        hasStock={product.purchasable}
        salePercentage={product.sale_percentage}
      />
      <div css={styles.productPrices}>
        <Price
          testId={RELATED_PRICE}
          isSalePrice={!!(product.on_sale && preSalePrice)}
          css={styles.productPrice}
          amount={price!.amount}
          currency={currency}
        />
        {product.on_sale && preSalePrice && (
          <Price
            testId={RELATED_PREVIOUS_PRICE}
            isPreviousPrice={true}
            css={styles.productPreviousPrice}
            {...preSalePrice}
          />
        )}
      </div>
    </div>
  );
};
